import { useState } from "react";
import { Link, createFileRoute } from "@tanstack/react-router";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { AuthFrame, AuthHead, Notice } from "@/components/AuthFrame";
import { CodeInput } from "@/features/auth/CodeInput";
import { PasswordFields } from "@/features/auth/PasswordFields";
import { authErrorText, newPasswordProblem } from "@/features/auth/errors";
import { signedIn, useGo } from "@/features/auth/afterSignIn";
import { ApiError } from "@/lib/api";
import { confirmPasswordReset } from "@/lib/auth";
import { forgetLinkToken, useLinkToken } from "@/lib/linkToken";
import { session } from "@/lib/session";

// A new password from the link in the mail (I-m-nieuw-wachtwoord): the password twice and the code from
// the app. Then every other session of theirs ends and they are signed in. The link works one hour.

export const Route = createFileRoute("/password")({
  beforeLoad: () => session.ended(),
  component: NewPasswordScreen,
});

function NewPasswordScreen() {
  const { t, i18n } = useTranslation();
  const queryClient = useQueryClient();
  const go = useGo();
  const token = useLinkToken("reset");
  const [password, setPassword] = useState("");
  const [repeat, setRepeat] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [invalid, setInvalid] = useState(!token);

  const submit = async () => {
    const problem = newPasswordProblem(t, password, repeat, null);
    if (problem) return setError(problem);
    if (!token || code.length !== 6) return;
    setBusy(true);
    setError(null);
    try {
      const me = await confirmPasswordReset(token, password, code);
      forgetLinkToken("reset");
      void go(signedIn(queryClient, me));
    } catch (err) {
      setCode("");
      if (err instanceof ApiError && err.code === "LINK_INVALID") setInvalid(true);
      else setError(authErrorText(t, err, i18n.language));
    } finally {
      setBusy(false);
    }
  };

  if (invalid) {
    return (
      <AuthFrame label="Portaal Link verlopen">
        <AuthHead title={t("link.invalidTitle")} lead={t("link.invalidReset")} />
        <div className="wl-auth__actions">
          <Link to="/login" className="wl-btn wl-btn--ghost">
            {t("common.backToSignIn")}
          </Link>
          <span className="wl-spacer" />
          <Link to="/forgot" className="wl-btn wl-btn--primary wl-btn--lg">
            {t("link.askNew")}
          </Link>
        </div>
      </AuthFrame>
    );
  }

  return (
    <AuthFrame label="Portaal Nieuw wachtwoord">
      <form
        className="wl-vstack"
        style={{ gap: "var(--space-5)", alignItems: "stretch" }}
        onSubmit={(event) => {
          event.preventDefault();
          void submit();
        }}
      >
        <AuthHead title={t("reset.title")} />
        {error && <Notice tone="danger">{error}</Notice>}
        <PasswordFields password={password} repeat={repeat} onPassword={setPassword} onRepeat={setRepeat} email={null} autoFocus />
        <div className="wl-field">
          <span className="wl-field__label">{t("code.label")}</span>
          <CodeInput value={code} onChange={setCode} label={t("code.label")} />
        </div>
        <div className="wl-auth__actions">
          <span className="wl-spacer" />
          <button type="submit" className="wl-btn wl-btn--primary wl-btn--lg" disabled={busy || password === "" || code.length !== 6}>
            {t("reset.submit")}
          </button>
        </div>
        <p className="wl-auth__foot">{t("reset.elsewhere")}</p>
        <p className="wl-auth__foot">{t("reset.noPhone")}</p>
      </form>
    </AuthFrame>
  );
}
